import { useCallback, useState } from "react";
import { Bridge } from "./components/Bridge";
import { GalaxyMap } from "./components/galaxy/GalaxyMap";
import { WarpTransition } from "./components/galaxy/WarpTransition";
import { LandingPage } from "./components/LandingPage";
import { AuthModal } from "./components/auth/AuthModal";
import { ReplayViewer } from "./components/replay/ReplayViewer";
import { MissionLogGallery } from "./components/replay/MissionLogGallery";
import { useGameMode } from "./hooks/useGameMode";
import { useGalaxyState } from "./hooks/useGalaxyState";
import { useAuth } from "./hooks/useAuth";

const SAVE_KEY = "starbridge-save-v1";

function App() {
  const auth = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "signup">("signup");

  const {
    gameMode,
    warpTarget,
    encounterSession,
    replaySessionId,
    initiateWarp,
    returnToMap,
    startReplay,
    setGameMode,
  } = useGameMode();

  const [showLogGallery, setShowLogGallery] = useState(false);

  const { galaxyState, isLoading, error } = useGalaxyState(auth.isAuthenticated);

  const hasSavedMission = (() => {
    try { return localStorage.getItem(SAVE_KEY) !== null; } catch { return false; }
  })();

  const [autoResume, setAutoResume] = useState(false);

  const resumeMission = useCallback(() => {
    setAutoResume(true);
    setGameMode("bridge");
  }, [setGameMode]);

  const handleWarp = useCallback((systemId: string, missionId?: string) => {
    const system = galaxyState?.systems.find(s => s.id === systemId);
    initiateWarp({
      systemId,
      systemName: system?.name ?? systemId,
      missionId,
    });
  }, [galaxyState, initiateWarp]);

  const openAuth = useCallback((mode: "login" | "signup") => {
    setAuthMode(mode);
    setShowAuth(true);
    auth.clearError();
  }, [auth.clearError]);

  const closeAuth = useCallback(() => {
    setShowAuth(false);
    auth.clearError();
  }, [auth.clearError]);

  // ── Auth loading state ──
  if (auth.isLoading) {
    return (
      <div className="bridge-start">
        <div className="start-content">
          <h1 className="start-title">STARBRIDGE</h1>
          <p className="start-subtitle">Bridge Command RPG</p>
          <div className="loading-text">AUTHENTICATING...</div>
        </div>
      </div>
    );
  }

  // ── Not authenticated → Landing page ──
  if (!auth.isAuthenticated) {
    return (
      <>
        <LandingPage
          onPlayNow={() => openAuth("signup")}
          onLogin={() => openAuth("login")}
        />
        {showAuth && (
          <AuthModal
            onClose={closeAuth}
            onSuccess={closeAuth}
            signup={auth.signup}
            login={auth.login}
            error={auth.error}
            clearError={auth.clearError}
            isLoading={auth.isBusy}
            initialMode={authMode}
          />
        )}
      </>
    );
  }

  // ── Authenticated → Game ──

  // Loading galaxy state
  if (isLoading && gameMode === "galaxy_map") {
    return (
      <div className="bridge-start">
        <div className="start-content">
          <h1 className="start-title">STARBRIDGE</h1>
          <p className="start-subtitle">Bridge Command RPG</p>
          <div className="loading-text">LOADING GALAXY STATE...</div>
        </div>
      </div>
    );
  }

  // Error loading galaxy state
  if (error && gameMode === "galaxy_map") {
    return (
      <div className="bridge-start">
        <div className="start-content">
          <h1 className="start-title">STARBRIDGE</h1>
          <p className="start-subtitle">Bridge Command RPG</p>
          <div className="start-ship" style={{ color: "var(--color-red)" }}>
            CONNECTION FAILED: {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Galaxy Map — default view */}
      {gameMode === "galaxy_map" && galaxyState && (
        <GalaxyMap
          galaxyState={galaxyState}
          onWarp={handleWarp}
          onResumeMission={hasSavedMission ? resumeMission : undefined}
          onMissionLogs={() => setShowLogGallery(true)}
          onLogout={auth.logout}
          displayName={auth.user?.displayName}
        />
      )}

      {/* Mission Log Gallery overlay */}
      {showLogGallery && gameMode === "galaxy_map" && (
        <MissionLogGallery
          onSelect={(id) => { setShowLogGallery(false); startReplay(id); }}
          onBack={() => setShowLogGallery(false)}
        />
      )}

      {/* Warp transition */}
      {gameMode === "warp_transition" && warpTarget && (
        <WarpTransition targetName={warpTarget.systemName} />
      )}

      {/* Bridge — encounter view */}
      {gameMode === "bridge" && (
        <Bridge
          encounterData={encounterSession}
          onReturnToMap={returnToMap}
          autoResume={autoResume}
        />
      )}

      {/* Replay viewer */}
      {gameMode === "replay" && replaySessionId && (
        <ReplayViewer
          sessionId={replaySessionId}
          onExit={returnToMap}
        />
      )}
    </>
  );
}

export default App;
